import React, { useState } from 'react';
import {
  Badge, Button, HStack, Popover, PopoverArrow, PopoverBody, PopoverContent,
  PopoverTrigger, Stack, Text, Tooltip, useToast,
} from '@chakra-ui/react';
import { FiSlack } from 'react-icons/fi';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import SlackInvitePicker from './SlackInvitePicker';
import SlackChannelSelect from './SlackChannelSelect';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

// item 379 — link ANY chat thread to a Slack channel (project talk / global talk /
// accountant talk; help desks have their own, item 374). Same behavior everywhere:
// name a channel → created-or-joined immediately when Slack is connected, reserved
// (PENDING badge) when it isn't. Messages then mirror both ways (373/378).
interface SlackLinkState {
  configured: boolean;
  channelId: string | null;
  channelName: string | null;
  desiredChannelName: string | null;
}

const SlackLinkButton: React.FC<{
  threadKey?: string;
  suggestedName: string;
  privateChannel?: boolean; // item 379 — the accountant thread links a private (invite-only) channel
}> = ({
  threadKey, suggestedName, privateChannel,
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const queryKey = ['slackLink', threadKey ?? 'accountant'];
  const { data } = useQuery<SlackLinkState>(queryKey, async () => {
    const { data: d } = await axios.get(
      `${apiUrl()}Chat/slack-link${threadKey ? `?threadKey=${encodeURIComponent(threadKey)}` : ''}`,
    );
    return d;
  });
  const [channel, setChannel] = useState('');
  const [invitees, setInvitees] = useState<string[]>([]); // item 386

  // the effective channel: a fresh pick, else the already-linked/reserved one
  const effectiveChannel = channel || data?.channelName || data?.desiredChannelName || '';

  const link = useMutation(
    () => axios.post(`${apiUrl()}Chat/slack-link`, {
      threadKey: threadKey ?? null,
      channelName: effectiveChannel.trim(),
      inviteUserIds: invitees, // item 386
    }),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(queryKey);
        const missed = (res.data?.noSlack ?? []) as string[];
        toast({
          status: res.data?.pending ? 'info' : 'success',
          title: res.data?.pending
            ? 'Channel reserved — links automatically once Slack is connected.'
            : `Linked to #${res.data?.channelName}${res.data?.invited ? ` — invited ${res.data.invited}` : ''}.`,
          description: missed.length > 0 ? `No Slack account found for: ${missed.join(', ')}` : undefined,
          duration: 7000,
          isClosable: true,
        });
      },
      onError: () => { toast({ status: 'error', title: 'Slack link failed' }); },
    },
  );

  return (
    <HStack spacing={2}>
      {data?.channelName && (
        <Badge colorScheme="green" fontSize="0.6rem">{`SLACK #${data.channelName}`}</Badge>
      )}
      {!data?.channelName && data?.desiredChannelName && (
        <Tooltip hasArrow label="Channel reserved — connects automatically once Slack is wired up.">
          <Badge colorScheme="yellow" fontSize="0.6rem">{`SLACK PENDING #${data.desiredChannelName}`}</Badge>
        </Tooltip>
      )}
      <Popover placement="bottom-end">
        <PopoverTrigger>
          <Button size="xs" variant="outline" leftIcon={<FiSlack />}>
            {data?.channelId ? 'Slack settings' : 'Link to Slack'}
          </Button>
        </PopoverTrigger>
        <PopoverContent w="300px">
          <PopoverArrow />
          <PopoverBody>
            <Text fontSize="xs" mb={2} opacity={0.75}>
              {(() => {
                if (!data?.configured) return 'Slack isn’t connected to Basix yet — channels will list once it is.';
                return privateChannel
                  ? 'Pick the private (invite-only) Slack channel to link. Create it in Slack and invite @basix first.'
                  : 'Pick the public Slack channel to link — messages then flow both ways. (Create a new channel in Slack first if you need one.)';
              })()}
            </Text>
            <Stack spacing={2}>
              {/* item 403c / 379 — choose an existing channel (public, or private for accountant) */}
              <SlackChannelSelect
                value={effectiveChannel}
                onChange={setChannel}
                initialFilter={suggestedName}
                privateOnly={privateChannel}
              />
              {/* item 386 — invite teammates while linking */}
              <SlackInvitePicker selected={invitees} onChange={setInvitees} />
              <Button
                size="sm"
                isLoading={link.isLoading}
                isDisabled={!effectiveChannel}
                onClick={() => link.mutate()}
              >
                Save
              </Button>
            </Stack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </HStack>
  );
};

SlackLinkButton.defaultProps = { threadKey: undefined, privateChannel: false };

export default SlackLinkButton;
